/**
 * Territory Canvas GP economy — FRONTEND mirror of the backend authority
 * (backend app/canvas/gp.py). Used ONLY for the live editor meter/preview.
 * The BACKEND is the final authority at SAVE (Work Order §29). This module
 * reproduces the exact HALF-GP integer arithmetic so the live meter matches
 * the authoritative server result for identical geometry (Work Order §5/§30).
 *
 * 1 half-unit = 0.5 GP. Per-module cost quantizes UPWARD to the next half-GP,
 * minimum 0.5 GP for any positive authorized-visible area. Used GP is the sum
 * of already-quantized per-module half-units.
 */
import { gridPowerCapacity } from "./grid-power";

export const GP_EPS = 1e-9;

const BASE_HALF: Record<string, number> = {
  TITLE: 2, TAGLINE: 2, DESCRIPTION: 6, LINK: 4, SOCIAL: 4,
  CONTACTS: 6, IMAGE: 8, PDF: 8, GIF: 14, VIDEO: 30, PIXEL_ART: 8,
};
const SHAPE_FILL_HALF = 2; // RECTANGLE / ROUNDED_RECTANGLE / ELLIPSE = 1 GP
const LINE_HALF = 2; // LINE fixed = 1 GP (Work Order §8)

/** Canonical full-instance base cost of a module, in half-GP units. */
export function moduleBaseHalf(type: string, shapeSubtype?: string | null): number {
  if (type === "SHAPE") {
    return shapeSubtype === "LINE" ? LINE_HALF : SHAPE_FILL_HALF;
  }
  return BASE_HALF[type] ?? 0;
}

/**
 * Quantized per-module cost in half-GP units for an AREA-based module given
 * its authorized-visible ratio in [0,1]. Matches gp.element_cost_half.
 */
export function quantizeHalf(baseHalf: number, ratio: number): number {
  if (baseHalf <= 0 || ratio <= 0) return 0;
  const r = ratio > 1 ? 1 : ratio;
  const charged = Math.ceil(baseHalf * r - GP_EPS);
  return charged >= 1 ? charged : 1;
}

/** LINE cost in half-GP units: fixed 1 GP while it has a visible portion. */
export function lineCostHalf(hasVisiblePortion: boolean): number {
  return hasVisiblePortion ? LINE_HALF : 0;
}

export function halfToGp(half: number): number {
  return half / 2;
}

/** Authoritative-mirror GP capacity for a Territory union area (world-units²). */
export function capacityGp(area: number): number {
  return gridPowerCapacity(area);
}

export interface GpMeter {
  capacityGp: number;
  usedGp: number;
  availableGp: number;
  overCapacity: boolean;
}

/** Build a live meter from a capacity (GP) and per-module half-unit costs. */
export function gpMeter(capacityGpValue: number, elementHalfCosts: number[]): GpMeter {
  const usedHalf = elementHalfCosts.reduce((a, b) => a + b, 0);
  const capHalf = capacityGpValue * 2;
  return {
    capacityGp: capacityGpValue,
    usedGp: halfToGp(usedHalf),
    availableGp: halfToGp(capHalf - usedHalf),
    overCapacity: usedHalf > capHalf,
  };
}

export interface RectLike { x: number; y: number; w: number; h: number }

/**
 * Rotation-aware axis-aligned bbox of a box module (mirror of backend
 * geometry.element_bbox). A rotated box's economic footprint is measured over
 * its ROTATED AABB (approved law §26/§68), so the live meter MUST use the same
 * extent the server enforces.
 */
export function boxAabb(box: RectLike, rotationDeg: number): RectLike {
  const rot = rotationDeg || 0;
  if (rot % 360 === 0) return { x: box.x, y: box.y, w: box.w, h: box.h };
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  const theta = (rot * Math.PI) / 180;
  const ct = Math.cos(theta);
  const st = Math.sin(theta);
  const corners: Array<[number, number]> = [
    [box.x, box.y], [box.x + box.w, box.y],
    [box.x, box.y + box.h], [box.x + box.w, box.y + box.h],
  ];
  const xs: number[] = [];
  const ys: number[] = [];
  for (const [px, py] of corners) {
    const dx = px - cx;
    const dy = py - cy;
    xs.push(cx + dx * ct - dy * st);
    ys.push(cy + dx * st + dy * ct);
  }
  const minx = Math.min(...xs);
  const miny = Math.min(...ys);
  return { x: minx, y: miny, w: Math.max(...xs) - minx, h: Math.max(...ys) - miny };
}

export function rectOverlapArea(a: RectLike, b: RectLike): number {
  const x2 = Math.min(a.x + a.w, b.x + b.w);
  const y2 = Math.min(a.y + a.h, b.y + b.h);
  const ow = x2 - Math.max(a.x, b.x);
  const oh = y2 - Math.max(a.y, b.y);
  return (ow > 0 ? ow : 0) * (oh > 0 ? oh : 0);
}

/**
 * Authorized-visible ratio of a module's extent (bbox) over the union of its
 * authorized parcels. Matches the backend rectangle path (visible_over / full
 * bbox) so the live meter equals the authoritative server result.
 */
export function visibleRatioBBox(bbox: RectLike, authorized: RectLike[]): number {
  const full = Math.abs(bbox.w) * Math.abs(bbox.h);
  if (full <= 0) return 0;
  let vis = 0;
  for (const p of authorized) vis += rectOverlapArea(bbox, p);
  const r = vis / full;
  return r > 1 ? 1 : r;
}

/** Per-module half-GP cost for the live meter (mirrors gp.element_cost_half). */
export function elementCostHalf(
  type: string,
  subtype: string | null | undefined,
  ratio: number,
  lineVisible: boolean,
): number {
  if (type === "SHAPE" && subtype === "LINE") return lineCostHalf(lineVisible);
  return quantizeHalf(moduleBaseHalf(type, subtype), ratio);
}

// ── EXACT rotated-ELLIPSE ∩ authorized-Territory area (Work Order §9) ──
// Bit-faithful mirror of backend app/canvas/ellipse.py: an ellipse inscribed in
// {x,y,w,h} rotated θ is the affine image of the UNIT CIRCLE; each authorized
// parcel rect is transformed into unit-circle space and its area ∩ the unit
// circle is computed analytically (sectors + chord triangles). No rasterization,
// no sampling. rx·ry cancels → ratio = (Σ unit intersections) / π. The bbox is
// only a broad-phase rejection, never the economic authority.

/** Canonical numeric-noise floor for the ellipse ratio (mirrors _RATIO_EPS). */
export const ELLIPSE_RATIO_EPS = 1e-9;

/** Signed area of triangle(O, a, b) ∩ unit disk (r=1). */
function triDiskUnit(ax: number, ay: number, bx: number, by: number): number {
  const r2 = 1.0;
  const a2 = ax * ax + ay * ay;
  const b2 = bx * bx + by * by;
  const sector = (ux: number, uy: number, vx: number, vy: number): number => {
    const ang = Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
    return 0.5 * r2 * ang;
  };
  if (a2 <= r2 && b2 <= r2) return 0.5 * (ax * by - ay * bx);
  const dx = bx - ax;
  const dy = by - ay;
  const A = dx * dx + dy * dy;
  const B = 2.0 * (ax * dx + ay * dy);
  const C = a2 - r2;
  const disc = B * B - 4.0 * A * C;
  if (A === 0.0 || disc <= 0.0) return sector(ax, ay, bx, by);
  const sq = Math.sqrt(disc);
  const t1 = (-B - sq) / (2.0 * A);
  const t2 = (-B + sq) / (2.0 * A);
  const lo = t1 > 0.0 ? t1 : 0.0;
  const hi = t2 < 1.0 ? t2 : 1.0;
  if (lo >= hi) return sector(ax, ay, bx, by);
  const px = ax + lo * dx;
  const py = ay + lo * dy;
  const qx = ax + hi * dx;
  const qy = ay + hi * dy;
  let total = 0.0;
  if (lo > 0.0) total += sector(ax, ay, px, py);
  total += 0.5 * (px * qy - py * qx);
  if (hi < 1.0) total += sector(qx, qy, bx, by);
  return total;
}

function polyUnitDiskArea(poly: Array<[number, number]>): number {
  let s = 0.0;
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const [ax, ay] = poly[i];
    const [bx, by] = poly[(i + 1) % n];
    s += triDiskUnit(ax, ay, bx, by);
  }
  return Math.abs(s);
}

function aabbIntersects(a: RectLike, b: RectLike): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * Exact authorized-visible ratio in [0,1] of a rotated ellipse (inscribed in
 * `box`) over the union of disjoint authorized parcel rects. `rects` MUST be in
 * the same deterministic order the backend uses (sorted parcel id).
 */
export function ellipseAuthorizedRatio(
  box: RectLike,
  rotationDeg: number,
  rects: RectLike[],
): number {
  const w = Math.abs(box.w);
  const h = Math.abs(box.h);
  const rx = w / 2.0;
  const ry = h / 2.0;
  if (rx <= 0.0 || ry <= 0.0) return 0.0;
  const cx = box.x + box.w / 2.0;
  const cy = box.y + box.h / 2.0;
  const theta = (rotationDeg || 0.0) * (Math.PI / 180.0);
  const ct = Math.cos(theta);
  const st = Math.sin(theta);
  const ex = Math.sqrt((rx * ct) ** 2 + (ry * st) ** 2);
  const ey = Math.sqrt((rx * st) ** 2 + (ry * ct) ** 2);
  const aabb: RectLike = { x: cx - ex, y: cy - ey, w: 2.0 * ex, h: 2.0 * ey };
  let totalUnit = 0.0;
  for (const r of rects) {
    if (!aabbIntersects(aabb, r)) continue;
    const corners: Array<[number, number]> = [
      [r.x, r.y], [r.x + r.w, r.y], [r.x + r.w, r.y + r.h], [r.x, r.y + r.h],
    ];
    const poly: Array<[number, number]> = corners.map(([X, Y]) => {
      const xp = X - cx;
      const yp = Y - cy;
      const ux = xp * ct + yp * st;
      const uy = -xp * st + yp * ct;
      return [ux / rx, uy / ry];
    });
    totalUnit += polyUnitDiskArea(poly);
  }
  const ratio = totalUnit / Math.PI;
  if (ratio <= ELLIPSE_RATIO_EPS) return 0.0;
  return ratio > 1.0 ? 1.0 : ratio;
}
